import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getQueuesSummary, QueueSummaryResponse } from "@/services/workerApi";
import { Activity } from "lucide-react";

const QUEUE_LABELS: Record<string, string> = {
  invite: "Invitations",
  message: "Messages",
  enrichment: "Enrichissement",
  search: "Recherche",
};

function countBadge(count: number) {
  if (count === 0) return "secondary";
  if (count < 10) return "outline";
  if (count < 50) return "default";
  return "destructive";
}

export function QueueSummaryCard() {
  const navigate = useNavigate();
  const [data, setData] = useState<QueueSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await getQueuesSummary(navigate, { top: 0 });
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const queues = data
    ? (["invite", "message", "enrichment", "search"] as const).map((key) => ({
        key,
        label: QUEUE_LABELS[key],
        pending: data[key]?.count ?? 0,
        processing: data[key]?.processing_count ?? 0,
        total: data[key]?.total_count ?? 0,
      }))
    : [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Files d'attente</CardTitle>
        <Activity className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground">Chargement...</div>
        ) : data ? (
          <div className="space-y-3">
            {queues.map((q) => (
              <div key={q.key} className="flex items-center justify-between">
                <span className="text-sm">{q.label}</span>
                <div className="flex items-center gap-2">
                  <Badge variant={countBadge(q.pending)} className="text-xs">
                    {q.pending} en attente
                  </Badge>
                  {q.processing > 0 && (
                    <Badge variant="outline" className="text-xs">
                      {q.processing} en cours
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-red-600">Erreur</div>
        )}
      </CardContent>
    </Card>
  );
}
